import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import { Divider, Drawer, IconButton } from '@mui/material';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import {
	headerHeight,
	topContainerHeight,
} from '../../data/globalStylingVariables';

const drawerWidth = 240;
const drawerHeight = `calc(100vh - ${headerHeight})`;

interface Props {
	leftSidebarContent: ReactNode;
	mainContent: ReactNode;
}

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			display: 'flex',
		},
		menuButton: {
			alignSelf: 'start',
			margin: '8px 0 0 0',
		},
		hide: {
			display: 'none',
		},
		drawer: {
			width: drawerWidth,
			flexShrink: 0,
			minHeight: drawerHeight,
			maxHeight: drawerHeight,
		},
		drawerPaper: {
			position: 'relative',
			width: drawerWidth,
		},
		drawerHeader: {
			display: 'flex',
			alignItems: 'center',
			padding: theme.spacing(0, 1),
			// necessary for content to be below app bar
			...theme.mixins.toolbar,
			justifyContent: 'flex-end',
			minHeight: topContainerHeight + ' !important',
		},
		content: {
			flexGrow: 1,
			transition: theme.transitions.create('margin', {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.leavingScreen,
			}),
			marginLeft: -drawerWidth,
		},
		contentShift: {
			transition: theme.transitions.create('margin', {
				easing: theme.transitions.easing.easeOut,
				duration: theme.transitions.duration.enteringScreen,
			}),
			marginLeft: 0,
		},
	}),
);

const LeftSidebarLayout = (props: Props) => {
	const { leftSidebarContent, mainContent } = props;
	const classes = useStyles();
	const [open, setOpen] = React.useState(true);

	const handleDrawerOpen = () => {
		setOpen(true);
	};

	const handleDrawerClose = () => {
		setOpen(false);
	};

	return (
		<>
			<div
				style={{
					display: 'flex',
					backgroundColor: '#FFFFFF',
					minHeight: `calc(100vh - ${headerHeight})`,
				}}
			>
				<IconButton
					aria-label='open drawer'
					onClick={handleDrawerOpen}
					edge='start'
					className={clsx(classes.menuButton, open && classes.hide)}
					size='medium'
				>
					<MenuIcon />
				</IconButton>
				<Drawer
					className={classes.drawer}
					variant='persistent'
					anchor='left'
					open={open}
					classes={{
						paper: classes.drawerPaper,
					}}
				>
					<div className={classes.drawerHeader}>
						<IconButton onClick={handleDrawerClose} size='medium'>
							<ChevronLeftIcon />
						</IconButton>
					</div>
					<Divider />
					<LeftSidebarContainer>{leftSidebarContent}</LeftSidebarContainer>
				</Drawer>
				<main
					className={clsx(classes.content, {
						[classes.contentShift]: open,
					})}
				>
					<MainContainer>{mainContent}</MainContainer>
				</main>
			</div>
		</>
	);
};

const LeftSidebarContainer = styled.div`
	box-sizing: border-box;
	padding-top: 20px;
	display: flex;
	flex-direction: column;
	background-color: #ffffff;
	height: 100%;
	overflow: hidden;
`;

const MainContainer = styled.div`
	min-width: 320px;
	height: 100%;
`;

export default LeftSidebarLayout;
